import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  Card,
  CardActionArea,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AutoFixHigh, CheckCircle, Close } from "@mui/icons-material";
import api from "../../api";
import { fetchAllSondasByJob } from "../../utils/sondaFetch";
import { calculateAkkClass } from "../../utils/akkUtils";
import { calculateStorieIndex } from "../../utils/storieUtils";
import { SEKMELER, duzSecenek } from "./bulkEditColumns";

/**
 * KİTLESEL (TOPLU) DEĞİŞTİRME SİHİRBAZI
 *
 * Hücre-hücre editörden FARKLI bir akıştır: tek bir yeni değeri, seçilen
 * TÜM kayıtların (veya katmanların) TEK bir alanına birden uygular.
 *
 * Adımlar:
 *   1) Tür        -> Profil / Profil Katmanı / Sonda / Sonda Katmanı
 *   2) Çalışma    -> hangi işe ait kayıtlar
 *   3) Kayıtlar   -> hangileri (liste + "Hepsini Seç")
 *   4) Alan       -> hangi alan değiştirilecek
 *   5) Değer      -> yeni değer + onay
 */

const ADIM_BASLIKLARI = ["Tür", "Çalışma", "Kayıtlar", "Alan", "Değer & Onay"];

const guvenliParse = (veri) => {
  if (!veri) return {};
  if (typeof veri === "object") return veri;
  try {
    return JSON.parse(veri);
  } catch (e) {
    return {};
  }
};

const baslangicDurum = {
  adim: 0,
  tip: null,
  jobs: [],
  jobsYukleniyor: false,
  job: null,
  kayitlar: [],
  kayitYukleniyor: false,
  seciliSatirlar: new Set(),
  satirArama: "",
  alanArama: "",
  seciliAlan: null,
  yeniDeger: "",
  uygulaniyor: false,
  sonuc: null,
};

export default function BulkMassEditWizard() {
  const [acik, setAcik] = useState(false);
  const [durum, setDurum] = useState(baslangicDurum);

  const {
    adim,
    tip,
    jobs,
    jobsYukleniyor,
    job,
    kayitlar,
    kayitYukleniyor,
    seciliSatirlar,
    satirArama,
    alanArama,
    seciliAlan,
    yeniDeger,
    uygulaniyor,
    sonuc,
  } = durum;

  const guncelle = (parca) => setDurum((d) => ({ ...d, ...parca }));

  const ayar = tip ? SEKMELER[tip] : null;
  const profilTarafi = tip === "profil" || tip === "profilKatman";
  const katmanModu = Boolean(ayar?.katman);

  // --- ÇALIŞMA LİSTESİ: sihirbaz açılınca bir kez çekilir ------------------
  useEffect(() => {
    if (!acik) return;
    const fetchJobs = async () => {
      guncelle({ jobsYukleniyor: true });
      try {
        const res = await api.get("/admin/jobs");
        guncelle({ jobs: res.data.success ? res.data.jobs || [] : [] });
      } catch (err) {
        console.error("Çalışmalar çekilemedi:", err);
        guncelle({ jobs: [] });
      } finally {
        guncelle({ jobsYukleniyor: false });
      }
    };
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik]);

  const ac = () => {
    setDurum({ ...baslangicDurum, seciliSatirlar: new Set() });
    setAcik(true);
  };

  const kapat = () => {
    if (uygulaniyor) return;
    setAcik(false);
  };

  const tipSec = (yeniTip) => {
    setDurum({
      ...baslangicDurum,
      jobs,
      seciliSatirlar: new Set(),
      tip: yeniTip,
    });
  };

  // --- KAYIT SATIRLARI (katman modunda her horizon ayrı satır) -------------
  const satirlarTumu = useMemo(() => {
    if (!katmanModu)
      return kayitlar.map((k) => ({
        satirId: String(k.id),
        kayit: k,
        ustNo: profilTarafi ? k.profil_no : k.sonda_no,
        baslik: (profilTarafi ? k.profil_no : k.sonda_no) || "—",
        altBaslik: profilTarafi ? k.ilce_mahalle || "" : "",
      }));

    const cikti = [];
    kayitlar.forEach((k) => {
      const veri = guvenliParse(k.full_data);
      const katmanlar = Array.isArray(veri.horizons) ? veri.horizons : [];
      const ustNo = (profilTarafi ? k.profil_no : k.sonda_no) || "—";
      katmanlar.forEach((h, i) => {
        const derinlik =
          h?.derinlik_min != null && h?.derinlik_max != null
            ? `${h.derinlik_min}-${h.derinlik_max} cm`
            : "";
        cikti.push({
          satirId: `${k.id}#${i}`,
          kayit: k,
          katmanIndex: i,
          ustNo,
          baslik: `${ustNo} — ${i + 1}. Katman`,
          altBaslik: [h?.sembol, derinlik].filter(Boolean).join(" · "),
        });
      });
    });
    return cikti;
  }, [kayitlar, katmanModu, profilTarafi]);

  const filtreliSatirlar = useMemo(() => {
    const q = satirArama.trim().toLocaleLowerCase("tr-TR");
    if (!q) return satirlarTumu;
    return satirlarTumu.filter((s) =>
      `${s.baslik} ${s.altBaslik || ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(q),
    );
  }, [satirlarTumu, satirArama]);

  const hepsiSeciliMi =
    filtreliSatirlar.length > 0 &&
    filtreliSatirlar.every((s) => seciliSatirlar.has(s.satirId));
  const kismiSeciliMi = filtreliSatirlar.some((s) =>
    seciliSatirlar.has(s.satirId),
  );

  // NOT: Secim kumesi EN GUNCEL durumdan (fonksiyonel guncelleme) turetilir.
  // Closure'daki eski kumeden turetilince ust uste gelen tiklamalar birbirinin
  // uzerine yazip secimin kaybolmasina yol aciyordu.
  const hepsiniSecToggle = () => {
    setDurum((d) => {
      const yeni = new Set(d.seciliSatirlar);
      const hepsiVar = filtreliSatirlar.every((s) => yeni.has(s.satirId));
      if (hepsiVar) filtreliSatirlar.forEach((s) => yeni.delete(s.satirId));
      else filtreliSatirlar.forEach((s) => yeni.add(s.satirId));
      return { ...d, seciliSatirlar: yeni };
    });
  };

  const satirToggle = (id) => {
    setDurum((d) => {
      const yeni = new Set(d.seciliSatirlar);
      if (yeni.has(id)) yeni.delete(id);
      else yeni.add(id);
      return { ...d, seciliSatirlar: yeni };
    });
  };

  // --- ALAN LİSTESİ: profil_no/sonda_no gibi UST SEVIYE alanlar HARIC ------
  // (Ayni degeri onlarca kayda YAZMAK numaralari birbirine esitler, bu
  // yuzden kitlesel degistirmede secilemezler.)
  const filtreliGruplarAlan = useMemo(() => {
    if (!ayar) return [];
    const q = alanArama.trim().toLocaleLowerCase("tr-TR");
    return ayar.gruplar
      .map((g) => ({
        ...g,
        alanlar: g.alanlar.filter(
          (a) =>
            !a.ustSeviye &&
            (!q || a.label.toLocaleLowerCase("tr-TR").includes(q)),
        ),
      }))
      .filter((g) => g.alanlar.length > 0);
  }, [ayar, alanArama]);

  const etkilenenKayitSayisi = useMemo(
    () => new Set([...seciliSatirlar].map((id) => id.split("#")[0])).size,
    [seciliSatirlar],
  );

  // --- ADIM GEÇİŞLERİ -------------------------------------------------------
  const ileriAktifMi = () => {
    if (adim === 0) return Boolean(tip);
    if (adim === 1) return Boolean(job);
    if (adim === 2) return seciliSatirlar.size > 0;
    if (adim === 3) return Boolean(seciliAlan);
    return false;
  };

  const ileri = async () => {
    if (adim === 1) {
      // Calisma secildi: bu is + turdeki TUM kayitlari cekiyoruz.
      guncelle({ kayitYukleniyor: true });
      try {
        let veri = [];
        if (profilTarafi) {
          const res = await api.get(`/admin/job-profiles/${job.id}`);
          veri = res.data.success ? res.data.data || [] : [];
        } else {
          veri = await fetchAllSondasByJob(job.id);
        }
        guncelle({
          kayitlar: veri,
          seciliSatirlar: new Set(),
          satirArama: "",
          kayitYukleniyor: false,
        });
      } catch (err) {
        alert(
          "Kayıtlar çekilemedi: " +
            (err.response?.data?.message || err.message),
        );
        guncelle({ kayitYukleniyor: false });
        return;
      }
    }
    guncelle({ adim: adim + 1 });
  };

  const geri = () => guncelle({ adim: Math.max(0, adim - 1) });

  // --- UYGULAMA --------------------------------------------------------------
  const uygula = async () => {
    if (!seciliAlan || seciliSatirlar.size === 0) return;

    const bosMu = yeniDeger === "" || yeniDeger === null;
    const onayMetni =
      `${seciliSatirlar.size} satırdaki (${etkilenenKayitSayisi} kayıt) ` +
      `"${seciliAlan.label}" alanı ${bosMu ? "BOŞ değere" : "yeni değere"} ` +
      `değiştirilecek. Bu işlem GERİ ALINAMAZ.\n\nOnaylıyor musunuz?`;
    if (!window.confirm(onayMetni)) return;

    guncelle({ uygulaniyor: true, sonuc: null });
    try {
      const tumSutunlar = [
        ...ayar.gruplar.flatMap((g) => g.alanlar),
      ];

      const birikim = {};
      const kayitBul = (id) => kayitlar.find((k) => String(k.id) === id);
      const birikimAl = (kayitId) => {
        if (!birikim[kayitId]) {
          const kayit = kayitBul(kayitId);
          if (!kayit) return null;
          birikim[kayitId] = {
            kayit,
            fullData: { ...guvenliParse(kayit.full_data) },
            ustNo: profilTarafi ? kayit.profil_no : kayit.sonda_no,
          };
        }
        return birikim[kayitId];
      };

      seciliSatirlar.forEach((satirId) => {
        const [kayitId, katmanIndexStr] = satirId.split("#");
        const hedef = birikimAl(kayitId);
        if (!hedef) return;

        if (katmanModu) {
          const i = Number(katmanIndexStr);
          const katmanlar = Array.isArray(hedef.fullData.horizons)
            ? [...hedef.fullData.horizons]
            : [];
          katmanlar[i] = { ...(katmanlar[i] || {}), [seciliAlan.key]: yeniDeger };
          hedef.fullData.horizons = katmanlar;
        } else {
          hedef.fullData[seciliAlan.key] = yeniDeger;
        }
      });
      void tumSutunlar; // (ileride ustSeviye alan destegi eklenirse kullanilacak)

      const paket = Object.values(birikim).map((h) => {
        if (profilTarafi) {
          let akk = h.kayit.akk_sonuc || "";
          let indeks = h.kayit.storie_index || 0;
          try {
            const a = calculateAkkClass(h.fullData);
            akk = `${a.finalClass}${a.subClassString}`;
          } catch (e) {}
          try {
            const st = calculateStorieIndex(h.fullData);
            if (st.finalIndex !== "NaN") indeks = parseFloat(st.finalIndex);
          } catch (e) {}
          h.fullData.profil_no = h.ustNo;
          return {
            id: h.kayit.id,
            profil_no: h.ustNo,
            full_data: h.fullData,
            akk_sonuc: akk,
            storie_index: indeks,
          };
        }
        h.fullData.sonda_no = h.ustNo;
        return { id: h.kayit.id, sonda_no: h.ustNo, full_data: h.fullData };
      });

      if (profilTarafi)
        await api.put("/admin/update-bulk-profiles", { profiles: paket });
      else await api.put("/admin/update-bulk-sondas", { sondas: paket });

      guncelle({
        uygulaniyor: false,
        sonuc: {
          basarili: true,
          mesaj: `✅ ${paket.length} kayıt güncellendi (${seciliSatirlar.size} satır).`,
        },
      });
    } catch (err) {
      guncelle({
        uygulaniyor: false,
        sonuc: {
          basarili: false,
          mesaj:
            "❌ Kaydetme başarısız: " +
            (err.response?.data?.message || err.message || "Sunucu hatası"),
        },
      });
    }
  };

  const yeniIslem = () => setDurum({ ...baslangicDurum, jobs, seciliSatirlar: new Set() });

  // --- DEĞER GİRİŞİ (alanın türüne göre) -------------------------------------
  const degerGirisiCiz = () => {
    if (!seciliAlan) return null;

    if (seciliAlan.tip === "onay") {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(yeniDeger)}
              onChange={(e) => guncelle({ yeniDeger: e.target.checked })}
            />
          }
          label={seciliAlan.label}
        />
      );
    }

    if (seciliAlan.secenekler) {
      const liste = duzSecenek(seciliAlan.secenekler);
      const mevcut = seciliAlan.cokSecim
        ? Array.isArray(yeniDeger)
          ? yeniDeger
          : []
        : (yeniDeger ?? "");
      return (
        <Select
          fullWidth
          multiple={Boolean(seciliAlan.cokSecim)}
          displayEmpty
          value={mevcut}
          onChange={(e) => guncelle({ yeniDeger: e.target.value })}
          renderValue={(v) => (Array.isArray(v) ? v.join(", ") : v || <em>— boş —</em>)}
          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
        >
          {!seciliAlan.cokSecim && (
            <MenuItem value="">
              <em>— boş —</em>
            </MenuItem>
          )}
          {liste.map((s) => (
            <MenuItem key={s} value={s} dense>
              {seciliAlan.cokSecim && (
                <Checkbox size="small" checked={mevcut.includes(s)} sx={{ p: 0.25, mr: 0.75 }} />
              )}
              {s}
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <TextField
        fullWidth
        label={`Yeni "${seciliAlan.label}" değeri`}
        multiline={seciliAlan.tip === "uzun"}
        rows={seciliAlan.tip === "uzun" ? 3 : 1}
        type={
          seciliAlan.tip === "sayi"
            ? "number"
            : seciliAlan.tip === "tarih"
              ? "date"
              : "text"
        }
        InputLabelProps={seciliAlan.tip === "tarih" ? { shrink: true } : undefined}
        value={yeniDeger ?? ""}
        onChange={(e) => guncelle({ yeniDeger: e.target.value })}
      />
    );
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<AutoFixHigh />}
        onClick={ac}
      >
        Kitlesel Değiştir
      </Button>

      <Dialog open={acik} onClose={kapat} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flexGrow: 1 }}>Kitlesel (Toplu) Değiştirme Sihirbazı</Box>
          <IconButton size="small" onClick={kapat} disabled={uygulaniyor}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stepper activeStep={adim} sx={{ mb: 3 }} alternativeLabel>
            {ADIM_BASLIKLARI.map((b) => (
              <Step key={b}>
                <StepLabel>{b}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ADIM 0: TÜR --------------------------------------------------- */}
          {adim === 0 && (
            <Stack spacing={1.5}>
              <Alert severity="info">
                Önce hangi verinin toplu değiştirileceğini seçin.
              </Alert>
              {Object.entries(SEKMELER).map(([key, s]) => (
                <Card
                  key={key}
                  variant="outlined"
                  sx={{
                    borderColor: tip === key ? "primary.main" : undefined,
                    borderWidth: tip === key ? 2 : 1,
                  }}
                >
                  <CardActionArea onClick={() => tipSec(key)} sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography fontWeight={700}>{s.etiket}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.katman
                            ? "Her satır bir katmandır (horizon)."
                            : "Her satır bir kayıttır."}
                        </Typography>
                      </Box>
                      {tip === key && <CheckCircle color="primary" />}
                    </Stack>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}

          {/* ADIM 1: ÇALIŞMA ------------------------------------------------- */}
          {adim === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <b>{ayar?.etiket}</b> için hangi çalışmadaki kayıtları
                değiştireceksiniz?
              </Alert>
              <Autocomplete
                options={jobs}
                loading={jobsYukleniyor}
                getOptionLabel={(o) => o.job_name || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={job}
                onChange={(e, v) => guncelle({ job: v })}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Çalışma Seçin"
                    autoFocus
                    InputProps={{
                      ...p.InputProps,
                      endAdornment: (
                        <>
                          {jobsYukleniyor ? (
                            <CircularProgress size={18} />
                          ) : null}
                          {p.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* ADIM 2: KAYITLAR ------------------------------------------------ */}
          {adim === 2 && (
            <Box>
              {kayitYukleniyor ? (
                <Stack alignItems="center" py={5}>
                  <CircularProgress />
                </Stack>
              ) : (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <TextField
                      size="small"
                      placeholder="Ara..."
                      value={satirArama}
                      onChange={(e) =>
                        guncelle({ satirArama: e.target.value })
                      }
                      sx={{ flexGrow: 1 }}
                    />
                    <Chip
                      label={`${seciliSatirlar.size} / ${satirlarTumu.length} seçili`}
                      size="small"
                      color={seciliSatirlar.size > 0 ? "primary" : "default"}
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hepsiSeciliMi}
                        indeterminate={!hepsiSeciliMi && kismiSeciliMi}
                        onChange={hepsiniSecToggle}
                      />
                    }
                    label={`Hepsini Seç (listelenen ${filtreliSatirlar.length} satır)`}
                  />
                  <List
                    dense
                    sx={{
                      maxHeight: 340,
                      overflowY: "auto",
                      border: "1px solid #eaecf0",
                      borderRadius: 1,
                    }}
                  >
                    {filtreliSatirlar.length === 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2 }}
                      >
                        {katmanModu
                          ? "Bu çalışmada katman (horizon) bulunamadı."
                          : "Bu çalışmada kayıt bulunamadı."}
                      </Typography>
                    )}
                    {filtreliSatirlar.map((s) => (
                      <ListItem key={s.satirId} disablePadding>
                        <ListItemButton
                          onClick={() => satirToggle(s.satirId)}
                          dense
                        >
                          <Checkbox
                            edge="start"
                            checked={seciliSatirlar.has(s.satirId)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={s.baslik}
                            secondary={s.altBaslik || null}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}

          {/* ADIM 3: ALAN ----------------------------------------------------- */}
          {adim === 3 && (
            <Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Alan ara..."
                value={alanArama}
                onChange={(e) => guncelle({ alanArama: e.target.value })}
                sx={{ mb: 1 }}
              />
              <List
                dense
                sx={{
                  maxHeight: 400,
                  overflowY: "auto",
                  border: "1px solid #eaecf0",
                  borderRadius: 1,
                }}
              >
                {filtreliGruplarAlan.map((g) => [
                  <ListSubheader
                    key={`${g.grup}-baslik`}
                    sx={{ fontSize: "0.72rem", fontWeight: 800 }}
                  >
                    {g.grup.toLocaleUpperCase("tr-TR")}
                  </ListSubheader>,
                  ...g.alanlar.map((a) => (
                    <ListItemButton
                      key={a.key}
                      selected={seciliAlan?.key === a.key}
                      onClick={() =>
                        guncelle({ seciliAlan: a, yeniDeger: "" })
                      }
                    >
                      <ListItemText primary={a.label} />
                      {seciliAlan?.key === a.key && (
                        <CheckCircle color="primary" fontSize="small" />
                      )}
                    </ListItemButton>
                  )),
                ])}
                {filtreliGruplarAlan.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    Alan bulunamadı.
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* ADIM 4: DEĞER & ONAY ---------------------------------------------- */}
          {adim === 4 && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>{seciliSatirlar.size}</b> satırdaki (
                <b>{etkilenenKayitSayisi}</b> kayıt){" "}
                <b>"{seciliAlan?.label}"</b> alanı aşağıda gireceğiniz DEĞERLE
                değiştirilecek. Bu işlem geri alınamaz.
              </Alert>
              {degerGirisiCiz()}

              {sonuc && (
                <Alert
                  severity={sonuc.basarili ? "success" : "error"}
                  sx={{ mt: 2 }}
                >
                  {sonuc.mesaj}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={kapat} disabled={uygulaniyor}>
            Kapat
          </Button>
          <Box sx={{ flexGrow: 1 }} />

          {sonuc?.basarili ? (
            <Button variant="contained" onClick={yeniIslem}>
              Yeni İşlem Başlat
            </Button>
          ) : (
            <>
              {adim > 0 && (
                <Button onClick={geri} disabled={uygulaniyor}>
                  Geri
                </Button>
              )}
              {adim < 4 && (
                <Button
                  variant="contained"
                  onClick={ileri}
                  disabled={!ileriAktifMi() || kayitYukleniyor}
                  startIcon={
                    kayitYukleniyor ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  İleri
                </Button>
              )}
              {adim === 4 && (
                <Tooltip title="Seçilen tüm satırlara aynı anda uygulanır">
                  <span>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={uygula}
                      disabled={uygulaniyor}
                      startIcon={
                        uygulaniyor ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      Uygula
                    </Button>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
